import { Skeleton, buttonVariants, sonner } from '@sr/ui-library';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { refreshListsAfterEmbeddedWorkflow } from '../api/refreshAfterEmbeddedWorkflow';
import { useEmbedAuthHandshake } from '../hooks/useEmbedAuthHandshake';
import { useRoyaltiesRegistrationClearance } from '../hooks/useRoyaltiesRegistrationClearance';
import {
  getMsr2Base,
  isTrustedMsr2Message,
  withEmbedAuthParams,
} from '../utils/embedAuth';
import {
  EMBED_WORKFLOW_SRC_PARAM,
  EMBED_WORKFLOW_TITLE_PARAM,
  type EmbeddedWorkflowOutcome,
  getEmbeddedWorkflowOutcome,
  getEmbeddedWorkflowTitleName,
  getTrustedIframeUrl,
  isForeignWorkspaceAddTitle,
  isMembershipGatedEmbed,
  withoutEmbedWorkflowParams,
} from '../utils/embedWorkflow';
import { SkeletonRegion } from './SkeletonRegion';

const { toast } = sonner;

export const useEmbeddedWorkflow = () => {
  // Owned here rather than by the component so the `event.source` check below
  // can never be separated from the listener it protects.
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const navigate = useNavigate();
  const { workspaceId } = useParams();
  // Carried across the close below: the host page may be relying on it.
  const { state } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [iframeHeight, setIframeHeight] = useState('100%');
  const [isLoading, setIsLoading] = useState(true);
  const embedSrc = searchParams.get(EMBED_WORKFLOW_SRC_PARAM);
  const workflowTitle = searchParams.get(EMBED_WORKFLOW_TITLE_PARAM);
  // The buttons that open these gate first; this catches a hand-typed or stale `?src=`.
  const isGatedEmbed = isMembershipGatedEmbed(embedSrc);
  const isForeignEmbed = isForeignWorkspaceAddTitle(embedSrc, workspaceId);
  const clearance = useRoyaltiesRegistrationClearance(embedSrc);
  const isMembershipCleared =
    !isGatedEmbed || (clearance.isCleared && !isForeignEmbed);
  const isMembershipPending =
    isGatedEmbed && !isForeignEmbed && clearance.isPending;
  const isMembershipRefused =
    isGatedEmbed && (isForeignEmbed || clearance.isRefused);
  // Tagged here rather than at `.src` assignment so the value this hook returns is
  // the one the iframe actually loads -- otherwise the tag is invisible to callers
  // and to tests.
  const iframeSrc = useMemo(() => {
    if (!isMembershipCleared) return undefined;

    const trustedUrl = getTrustedIframeUrl(embedSrc);

    return trustedUrl ? withEmbedAuthParams(trustedUrl) : trustedUrl;
  }, [embedSrc, isMembershipCleared]);
  const titlesPath = workspaceId
    ? `/workspace/${workspaceId}/titles`
    : undefined;

  const showWorkflowToast = useCallback(
    (outcome: EmbeddedWorkflowOutcome, titleName = 'Your') => {
      let message = '';
      let title = '';

      switch (outcome) {
        case 'completed':
          message = `${titleName} title has been registered, you can access it on the titles page shortly.`;
          title = 'Successfully Registered';
          break;
        case 'draft':
          message = `${titleName} title has been saved as a draft to resume at a later time. It will become available shortly.`;
          title = 'Draft Saved';
          break;
        default:
          message = `${titleName} title workflow has been closed.`;
          break;
      }

      // eslint-disable-next-line prefer-const -- lint-backlog: assigned after the action closure that reads it is built; making it const needs a restructure
      let toastId: number | string | undefined;
      const action =
        outcome === 'completed' && titlesPath
          ? { label: 'View Titles', onClick: () => navigate(titlesPath) }
          : outcome === 'draft'
            ? {
                label: 'Dismiss',
                onClick: () => {
                  if (toastId !== undefined) {
                    toast.dismiss(toastId);
                  }
                },
              }
            : undefined;

      toastId = toast.success(title, {
        action,
        classNames: {
          actionButton: buttonVariants({
            className: '!text-ui-success !border-ui-success',
            overrideClass: true,
            size: 'sm',
            variant: 'outline',
          }),
        },
        description: message,
      });
    },
    [navigate, titlesPath],
  );

  const removeEmbedSrc = useCallback(
    (outcome?: EmbeddedWorkflowOutcome, titleName?: string) => {
      // Every outcome closes the workflow in place. Stepping back through
      // history instead is unreliable here: each navigation MSR2 makes inside
      // the iframe adds an entry to the joint session history, so `-1` usually
      // only rewinds the iframe. Replace rather than push, so the `?src=` entry
      // does not survive to re-open the form the user just finished with.
      setSearchParams(withoutEmbedWorkflowParams, { replace: true, state });

      if (outcome && outcome !== 'closed') {
        showWorkflowToast(outcome, titleName);
      }
    },
    [setSearchParams, showWorkflowToast, state],
  );

  const revealIframe = useCallback(() => {
    setIsLoading(false);
  }, []);

  // Closed without a toast: the host page's own gated actions say why.
  useEffect(() => {
    if (isMembershipRefused) removeEmbedSrc();
  }, [isMembershipRefused, removeEmbedSrc]);

  useEffect(() => {
    setIsLoading(true);
    setIframeHeight('100%');
  }, [iframeSrc]);

  // Runs on every way out of an open embed: an outcome message, the page's
  // Back, browser Back or a link elsewhere. MSR2 may have saved a step by then,
  // and a refetch on a cancel costs little.
  useEffect(() => {
    if (!iframeSrc) return;

    return () => refreshListsAfterEmbeddedWorkflow();
  }, [iframeSrc]);

  useEffect(() => {
    if (!iframeSrc) return;

    const handleMessage = (event: MessageEvent) => {
      // A spoofed `close`/`completed` could drive navigation and a success toast.
      if (!isTrustedMsr2Message(event, iframeRef.current) || !event.data) {
        return;
      }

      if (event.data.type === 'setHeight' && event.data.scrollHeight) {
        setIframeHeight(`${event.data.scrollHeight}px`);
      }

      const workflowOutcome = getEmbeddedWorkflowOutcome(event.data);

      if (workflowOutcome) {
        removeEmbedSrc(
          workflowOutcome,
          getEmbeddedWorkflowTitleName(workflowTitle),
        );
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [iframeSrc, removeEmbedSrc, workflowTitle]);

  return {
    iframeHeight,
    iframeRef,
    iframeSrc,
    isLoading,
    isMembershipPending,
    revealIframe,
  };
};

/**
 * Stand-in for the MSR2 form the iframe is fetching. The iframe reports its own
 * height only once it has loaded, so the placeholder is what holds the panel
 * open until then -- it mirrors the shape of a workflow step (heading, a run of
 * labelled fields, an action row) so the swap to the real form is a change of
 * colour rather than a jump of layout.
 */
const EmbeddedWorkflowSkeleton = () => (
  <SkeletonRegion label="Loading workflow" className="flex grow flex-col gap-8">
    <div className="space-y-3">
      <Skeleton className="h-7 w-1/3" />

      <Skeleton className="h-4 w-2/3" />
    </div>

    <div className="space-y-6">
      {Array.from({ length: 4 }).map((_, index) => (
        <div key={index} className="space-y-2">
          <Skeleton className="h-4 w-32" />

          <Skeleton className="h-9 w-full" />
        </div>
      ))}
    </div>

    <div className="flex justify-end gap-4">
      <Skeleton className="h-9 w-28" />

      <Skeleton className="h-9 w-36" />
    </div>
  </SkeletonRegion>
);

export const EmbeddedWorkflowView = () => {
  const {
    iframeHeight,
    iframeRef,
    iframeSrc,
    isLoading,
    isMembershipPending,
    revealIframe,
  } = useEmbeddedWorkflow();

  // MSR2 cannot reach its own Cognito session from a cross-site iframe, so it
  // asks this window for one. See utils/embedAuth.ts (APP-2221).
  useEmbedAuthHandshake(iframeRef, Boolean(iframeSrc));

  useEffect(() => {
    if (!iframeRef.current || !iframeSrc) return;

    iframeRef.current.src = iframeSrc;
  }, [iframeSrc]);

  if (!iframeSrc && !isMembershipPending) {
    return null;
  }

  return (
    <>
      <link href={getMsr2Base()} rel="preconnect" />

      <div className="flex h-full w-full flex-col rounded-xl border border-sr-outline bg-white p-5">
        {isLoading && <EmbeddedWorkflowSkeleton />}

        {iframeSrc && (
          <iframe
            // Named by aria-label, not title: browsers paint a title as a hover
            // tooltip over the frame while MSR2 is still booting inside it.
            aria-label="Workflow"
            className={`block w-full max-w-full ${isLoading ? 'invisible h-0' : ''}`}
            ref={iframeRef}
            style={{
              border: 'none',
              height: isLoading ? 0 : iframeHeight,
              maxWidth: '100%',
              minHeight: isLoading ? 0 : '200px',
              width: '100%',
            }}
            onLoad={revealIframe}
          />
        )}
      </div>
    </>
  );
};
